import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import apiClient from "../../../shared/API/apiClient";
import { useReportingYear } from "../../../context/ReportingYearContext";
import "../../../styles/pages/EmissionIntensityListing.css";

const fmt = (val, decimals = 2) =>
  val !== null && val !== undefined && val !== "" && !isNaN(Number(val))
    ? Number(val).toLocaleString("en-IN", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : "0.00";

const EmissionIntensityListing = () => {
  const { reportingYearId } = useReportingYear();
  const location = useLocation();
  const navigate = useNavigate();
  const { title, entityId } = location.state || {};

  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!reportingYearId) return;
    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await apiClient.get("/api/environmental-summary", {
          params: { reporting_year_id: reportingYearId, legal_entity_id: entityId },
        });
        if (res.data.success) setApiData(res.data);
        else setError(res.data.message || "Failed to load intensity data.");
      } catch (err) {
        setError("An error occurred while fetching intensity data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportingYearId, entityId]);

  if (loading) return <div className="intensity-page">Loading...</div>;
  if (error) return <div className="intensity-page" style={{color: 'red'}}>{error}</div>;

  const intensityValue = apiData?.emission_intensity ?? 0;
  const scopes = apiData?.scopes ?? [];
  const totalEmissions = apiData?.total_emissions ?? scopes.reduce((acc, s) => acc + (parseFloat(s.total_emissions) || 0), 0);
  const totalEnergy = apiData?.total_energy ?? 0;

  return (
    <div className="intensity-page">
      

      {apiData && (
        <>
          <div className="hero-card">
            <div className="icon-badge-blue">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <div>
              <div className="hero-title">{title || "Emission Intensity"}</div>
              <div className="hero-subtitle">Efficiency of emissions per unit of energy</div>
              <div className="hero-value">
                {fmt(intensityValue, 4)}<span className="hero-unit">tCO₂e / GJ</span>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-label">Calculation Breakdown</div>
            <div className="breakdown-grid">
              {scopes.filter(s => s.scope !== "none").map((scope) => (
                <div key={scope.scope} className="metric-box">
                  <div className="metric-label">{scope.title}</div>
                  <div className="metric-value">
                    {fmt(scope.total_emissions)} <span className="metric-unit">tCO₂e</span>
                  </div>
                </div>
              ))}
              <div className="metric-box" style={{background: '#f9fafb'}}>
                <div className="metric-label">TOTAL EMISSIONS</div>
                <div className="metric-value">
                  {fmt(totalEmissions)} <span className="metric-unit">tCO₂e</span>
                </div>
              </div>
              <div className="metric-box" style={{background: '#f9fafb'}}>
                <div className="metric-label">TOTAL ENERGY</div>
                <div className="metric-value">
                  {fmt(totalEnergy)} <span className="metric-unit">GJ</span>
                </div>
              </div>
            </div>
          </div>

           <div className="section-card">
            <div className="section-label">How It's Calculated</div>
            <div className="formula-container">
              <div className="fraction-box">
                <div className="fraction-text">Total Emissions ({fmt(totalEmissions)} tCO₂e)</div>
                <div className="fraction-line" />
                <div className="fraction-text">Total Energy ({fmt(totalEnergy)} GJ)</div>
              </div>
              <div className="equals-sign">=</div>
              <div className="result-box">
                <div className="result-value">{fmt(intensityValue, 4)}</div>
                <div className="metric-unit">tCO₂e / GJ</div>
              </div>
            </div>
            <div className="info-note">
              ℹ️ Emission Intensity measures environmental efficiency. A lower value indicates better performance.
            </div>
          </div>
        </>
      )}
      <div className="ws-back">
<button onClick={() => navigate(-1)} className="back-link">
        ← Back to Environmental Summary
      </button>
      </div>
    </div>
  );
};

export default EmissionIntensityListing;